import { useCallback, useMemo, useState } from 'react';
import { influencer } from '@round/api';
import { ValueType } from 'react-select';
import { useAbortableEffect } from '@round/utils';
import { showNotification } from '@/utility/showNotification';

export type InfluencerLocationOption = {
    value: number;
    label: string;
    countryCode: string;
    country: string;
    city: string;
    state: string;
    type: string;
};

// Prod rows can arrive with null fields despite the type; coerce so one bad row can't throw.
export const makeLocationOption = (location: influencer.InfluencerLocation): InfluencerLocationOption => {
    const countryName = location.country_name ?? '';
    const city = location.city ?? '';
    const state = location.state ?? '';
    return {
        value: location.id,
        label: `${countryName}${city ? `, ${city}` : ''}${state ? ', ' + state : ''}`,
        countryCode: location.country_code ?? '',
        country: countryName,
        city,
        state,
        type: location.type,
    };
};

// Minimal trailing debounce (avoids a lodash dependency for this one call site).
function debounce<TArgs extends unknown[]>(fn: (...args: TArgs) => void, wait: number) {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    return (...args: TArgs) => {
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(() => fn(...args), wait);
    };
}

type Params = Pick<
    Parameters<typeof influencer.getInfluencerLocations>[0],
    'country_code' | 'ordering' | 'state' | 'type'
> & {
    /** Override the page size, e.g. to load the full (small, finite) country list at once. */
    pageSize?: number;
};

const PAGE_SIZE = 25;

export function useInfluencerLocationSelect<IsMulti extends boolean = false>({
    country_code,
    state,
    ordering,
    type,
    pageSize = PAGE_SIZE,
}: Params = {}) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [locations, setLocations] = useState<influencer.InfluencerLocation[]>([]);
    const [locationSelection, setLocationSelection] = useState<ValueType<InfluencerLocationOption, IsMulti>>(null);

    const fetchData = useCallback(
        async (params: Parameters<typeof influencer.getInfluencerLocations>[0], requestInit?: RequestInit) => {
            try {
                setIsLoading(true);
                const response = await influencer.getInfluencerLocations(params, requestInit);

                if (response.status === 404) {
                    showNotification(response.data.detail, 'error');
                    return;
                }

                const validRows = response.data.results.filter((row) => row && typeof row.id === 'number');
                setLocations((prev) => prev.concat(validRows));
                setHasNextPage(!!response.data.next);
            } catch (e) {
                if ((e instanceof DOMException || e instanceof Error) && e.name === 'AbortError') {
                    return;
                }

                const errorMessage = e instanceof Error ? e.message : 'Could not fetch influencer locations';
                showNotification(errorMessage, 'error');
            } finally {
                setIsLoading(false);
                setIsSearchLoading(false);
            }
        },
        []
    );

    const debouncedFetchData = useMemo(() => debounce(fetchData, 700), [fetchData]);

    useAbortableEffect(
        (signal) => {
            const params = { search, page, page_size: pageSize, country_code, ordering, state, type };
            // Only debounce search keystrokes (non-empty search, page reset to 1); initial load,
            // pagination, and param changes fetch immediately so the picker isn't stalled 700ms.
            if (search && page === 1) {
                debouncedFetchData(params, { signal });
            } else {
                fetchData(params, { signal });
            }
        },
        [debouncedFetchData, fetchData, page, search, country_code, ordering, state, type, pageSize]
    );

    const reset = useCallback(() => {
        setPage(1);
        setLocations([]);
        setHasNextPage(false);
        setIsLoading(false);
        setIsSearchLoading(false);
    }, []);

    const options = useMemo(() => locations.map((location) => makeLocationOption(location)), [locations]);

    return {
        selectProps: {
            value: locationSelection,
            onChange: setLocationSelection,
            options,
            inputValue: search,
            onInputChange: (value: string) => {
                if (value === search) {
                    return;
                }

                reset();
                setSearch(value);

                if (value) {
                    setIsSearchLoading(true);
                }
            },
            onMenuScrollToBottom: () => {
                if (!hasNextPage) {
                    return;
                }
                setPage((prev) => prev + 1);
            },
            isLoading: isLoading || isSearchLoading,
            filterOption: null,
        },
        reset,
    };
}
